import React, { useState, useEffect } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Box from '@mui/joy/Box';
import Link from '@mui/joy/Link';
import CircularProgress from '@mui/joy/CircularProgress';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (latitude: number, longitude: number, name?: string) => void;
  isLoading: boolean;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  useEffect(() => {
    if (isOpen && navigator.geolocation) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setIsGettingLocation(false);
        },
        () => {
          setIsGettingLocation(false);
        },
      );
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (latitude !== null && longitude !== null) {
      onConfirm(latitude, longitude, name || undefined);
      setLatitude(null);
      setLongitude(null);
      setName('');
    }
  };

  const handleClose = () => {
    setLatitude(null);
    setLongitude(null);
    setName('');
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: 440, width: '100%' }}
        aria-labelledby="location-title"
      >
        <ModalClose />
        <Typography id="location-title" level="title-lg" startDecorator={<LocationOnIcon />}>
          Compartir ubicación
        </Typography>
        {isGettingLocation ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1 }}>
            <CircularProgress />
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Obteniendo tu ubicación...
            </Typography>
          </Box>
        ) : latitude !== null && longitude !== null ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  Latitud
                </Typography>
                <Input
                  type="number"
                  value={latitude}
                  onChange={(e) => setLatitude(parseFloat(e.target.value))}
                  slotProps={{ input: { step: 0.000001 } }}
                />
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  Longitud
                </Typography>
                <Input
                  type="number"
                  value={longitude}
                  onChange={(e) => setLongitude(parseFloat(e.target.value))}
                  slotProps={{ input: { step: 0.000001 } }}
                />
              </Box>
            </Box>
            <Link
              href={`https://maps.google.com/?q=${latitude},${longitude}`}
              target="_blank"
              level="body-sm"
            >
              Ver en Google Maps
            </Link>
            <Input
              placeholder="Nombre del lugar (opcional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </>
        ) : (
          <Typography level="body-sm" sx={{ color: 'neutral.500', textAlign: 'center', py: 2 }}>
            No se pudo obtener la ubicación
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="plain" color="neutral" onClick={handleClose} disabled={isLoading || isGettingLocation}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={isLoading || isGettingLocation || latitude === null}
            loading={isLoading}
          >
            Enviar
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default LocationPicker;
